/**
 * SectionDetail — Expanded section with items, wrapping ModelSectionDetail and StructureChartEditor.
 * Phase 3B: removed reloadState prop. Components that need to trigger re-renders
 * use TanStack Query cache updates instead.
 */

import React from 'react';
import { Box } from '@mui/material';
import ModelSectionDetail from '../../../components/ModelSectionDetail';
import StructureChartEditor from '../../../components/StructureChartEditor';
import { ModelSection, ModelSectionType } from '../../../types/ModelSection';

interface SectionDetailProps {
    section: ModelSection;
    sectionDataLoading: string | null;
    currentState: any;
    currentSensitivity: any;
    onDragEnd: (result: any, variant: 'Input' | 'Output') => void;
    onBack: () => void;
    scenarioId: number;
    recreateExcelInputIfSync: () => void;
    showDiffToggled: boolean;
    setShowDiffToggled: (v: boolean) => void;
    searchStrings: { Input: string; Output: string };
    setSearchStrings: (val: { Input: string; Output: string }) => void;
    userRights: any;
    modelDataSelected: any;
    setModelDataSelected: (item: any) => void;
    sensitivityModifiedIds: Set<string>;
    reloadModelDatas: (editingId?: string) => Promise<void>;
}

export default function SectionDetail({
    section,
    sectionDataLoading,
    currentState,
    currentSensitivity,
    onDragEnd,
    onBack,
    scenarioId,
    recreateExcelInputIfSync,
    showDiffToggled,
    setShowDiffToggled,
    searchStrings,
    setSearchStrings,
    userRights,
    modelDataSelected,
    setModelDataSelected,
    sensitivityModifiedIds,
    reloadModelDatas,
}: SectionDetailProps) {
    if (sectionDataLoading === section.config.id) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 200 }}>
                <div className="flex flex-col items-center gap-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="text-sm text-gray-500">Loading {section.config.name}...</span>
                </div>
            </Box>
        );
    }

    if (section.config.id === ModelSectionType.COMPANY) {
        return (
            <StructureChartEditor
                section={section}
                onBack={onBack}
                currentState={currentState}
                currentSensitivity={currentSensitivity}
                scenario_id={String(scenarioId)}
                userRights={userRights}
                reloadState={reloadModelDatas}
            />
        );
    }

    return (
        <ModelSectionDetail
            section={section}
            onBack={onBack}
            currentState={currentState}
            currentSensitivity={currentSensitivity}
            reloadState={reloadModelDatas}
            onDragEnd={onDragEnd}
            scenario_id={scenarioId}
            recreateExcelInputIfSync={recreateExcelInputIfSync}
            showDiffToggled={showDiffToggled}
            setShowDiffToggled={setShowDiffToggled}
            searchStrings={searchStrings}
            setSearchStrings={setSearchStrings}
            userRights={userRights}
            modelDataSelected={modelDataSelected}
            setModelDataSelected={setModelDataSelected}
            sensitivityModifiedIds={sensitivityModifiedIds}
        />
    );
}
